// Rung 3 of the disclosure ladder (DETAIL_LEVELS.md) on a player's points:
// where the number came from, stat by stat, in this league's scoring.
//
// A score is the number a manager most wants to argue with — "14.2? He only
// had 45 yards" — and the answer is arithmetic, not opinion: 45 rushing yards
// at 0.1 is 4.5, a touchdown is 6, two catches are 2 in PPR. The server folds
// the scorer's own items (`breakdown` on a lineup or matchup row), so the
// popover can never explain a score the engine did not make.
//
// Available at every detail level, like every popover: the level controls the
// face of a row, never whether a number can explain itself.

import type { ReactNode } from 'react';
import type { PlayerSummary, ScoreItem } from '@fsp/domain';
import { Why, type WhyRow } from '../../kit/Why';

/** A stat's count as the popover says it: whole when it is whole. */
function count(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** Points with their sign, to the hundredth only when the hundredth is there. */
export function signedPoints(points: number): string {
  const abs = Math.abs(Math.round(points * 100) / 100);
  const text = Number.isInteger(abs * 10) ? abs.toFixed(1) : abs.toFixed(2);
  return `${points < 0 ? '−' : '+'}${text}`;
}

/** The breakdown as popover rows: the stat, how many, and what they earned. */
export function breakdownRows(items: readonly ScoreItem[]): WhyRow[] {
  return items.map((i) => ({
    label: i.label,
    note: count(i.value),
    value: signedPoints(i.points),
  }));
}

/** The popover's rows for a score: its parts, and the league's rounding when it rounds. */
export function pointsRows(points: number, items: readonly ScoreItem[]): WhyRow[] {
  // A 0 with nothing behind it: his game is in and he has no line, or nothing
  // he did scores here. Said, not left as an empty box.
  if (items.length === 0)
    return [
      { label: points === 0 ? 'Nothing that scores here' : 'Points', value: points.toFixed(1) },
    ];
  const rows = breakdownRows(items);
  // A LEAGUE THAT ROUNDS ITS SCORES (to whole points, or to one place): the
  // parts are each stat's exact share and the score is rounded once, so the
  // difference is a row of its own and the rows add up to the score (the
  // review of 2026-10-11).
  const sum = items.reduce((n, i) => n + i.points, 0);
  const rounding = Math.round((points - sum) * 100) / 100;
  if (rounding !== 0)
    rows.push({ label: "Rounded, by the league's rules", value: signedPoints(rounding) });
  return rows;
}

export function PointsWhy({
  player,
  points,
  breakdown,
  statLine,
  children,
}: {
  player: PlayerSummary | null;
  /** The row's points. Null is a wait, and a wait has nothing to explain. */
  points: number | null;
  breakdown: readonly ScoreItem[] | null | undefined;
  statLine: string | null;
  children: ReactNode;
}) {
  if (!player || points === null) return <>{children}</>;
  const rows = pointsRows(points, breakdown ?? []);
  return (
    <Why
      subject={player.name}
      value={points.toFixed(1)}
      caption={`${points.toFixed(1)} points, stat by stat`}
      label={`${player.name}, ${points.toFixed(1)} points: how they were scored`}
      rows={rows}
      footnote={statLine ? `${statLine} · this league's scoring` : "This league's scoring"}
    >
      {children}
    </Why>
  );
}
